import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import type { ApiResponse } from "@/types/common";
import type { RefreshTokenRequest, TokenDto } from "@/types/auth";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./tokenStorage";

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5068/api/v1";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise: Promise<string | null> | null = null;

// Kept in sync with AuthContext. Imported as a string to avoid a circular
// dependency between the http client and the React context.
const AUTH_LOGOUT_EVENT = "hyperlocal:auth-logout";

function forceLogout() {
  clearTokens();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_LOGOUT_EVENT));
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!accessToken || !refreshToken) {
    forceLogout();
    return null;
  }

  try {
    const payload: RefreshTokenRequest = { accessToken, refreshToken };
    const response = await axios.post<ApiResponse<TokenDto>>(
      `${API_BASE_URL}/Auth/refresh-token`,
      payload,
      { headers: { "Content-Type": "application/json" } },
    );

    if (!response.data.success || !response.data.data) {
      forceLogout();
      return null;
    }

    const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data;
    setTokens(newAccessToken, newRefreshToken);
    return newAccessToken;
  } catch {
    forceLogout();
    return null;
  }
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    const newToken = await refreshPromise;
    if (!newToken) {
      forceLogout();
      return Promise.reject(error);
    }

    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    return apiClient(originalRequest);
  },
);

export function getApiErrorMessage(error: unknown, fallback = "Something went wrong"): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as any;
    if (data?.message) return data.message;
    if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(", ");
    if (data?.errors && typeof data.errors === "object") {
      const errMsgs = Object.values(data.errors).flat().filter(Boolean);
      if (errMsgs.length) return String(errMsgs.join(", "));
    }
    if (data?.title) return data.title;
    if (error.message) return error.message;
  }

  if (error instanceof Error) return error.message;
  return fallback;
}

export async function unwrapApiResponse<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  const { data } = await promise;
  if (!data.success) {
    throw new Error(data.message || "Request failed");
  }
  return data.data as T;
}
